import React, { useState, useRef, useEffect } from 'react';
import { FiSun, FiMoon, FiMenu, FiX, FiCamera, FiFileText, FiGlobe, FiChevronDown } from 'react-icons/fi';
import { useLocation, Link } from 'react-router-dom';

interface HeaderProps {
  darkMode: boolean;
  onThemeToggle: () => void;
}

const Header: React.FC<HeaderProps> = ({ darkMode, onThemeToggle }) => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPhotoMenuOpen, setIsPhotoMenuOpen] = useState(false);
  const photoMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (photoMenuRef.current && !photoMenuRef.current.contains(event.target as Node)) {
        setIsPhotoMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const mainNavigation = [
    { name: 'Countries', path: '/countries' },
    { name: 'About', path: '/about' },
    { name: 'Privacy Policy', path: '/privacy' },
  ];

  const photoFeatures = [
    { name: 'Photo Editor', path: '/photo-editor', icon: FiCamera, desc: 'Remove & replace backgrounds' },
    { name: 'Print Layout', path: '/print-layout', icon: FiFileText, desc: 'Create print-ready PDFs' },
    { name: 'Passport Sizes', path: '/passport-sizes', icon: FiGlobe, desc: 'Country-specific dimensions' },
  ];

  const isActive = (path: string) => location.pathname === path;
  const isPhotoActive = () => photoFeatures.some(f => isActive(f.path)) || isActive('/features');

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md border-b transition-all duration-300 bg-white/80 dark:bg-slate-900/80 border-gray-200/50 dark:border-gray-800/50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center cursor-pointer group"
            onClick={() => setIsMenuOpen(false)}
          >
            <img 
              src="/logo..PNG" 
              alt="travelbaba786 Logo" 
              className="h-10 w-auto transition-all duration-300 group-hover:scale-110"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="flex max-md:hidden items-center gap-2">
            {/* Photo Features Dropdown */}
            <div className="relative" ref={photoMenuRef}>
              <button
                onClick={() => setIsPhotoMenuOpen(!isPhotoMenuOpen)}
                className={`flex items-center gap-1 px-4 py-2 text-sm font-bold rounded-lg transition-all duration-300 ${
                  isPhotoActive()
                    ? 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-400/10'
                    : 'text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-slate-800'
                }`}
              >
                <FiCamera className="mr-1" />
                Photo Editor
                <FiChevronDown className={`transition-transform ${isPhotoMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {isPhotoMenuOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
                  {photoFeatures.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.path}
                        onClick={() => setIsPhotoMenuOpen(false)}
                        className={`flex items-start gap-3 px-4 py-3 transition-colors ${
                          isActive(item.path)
                            ? 'bg-blue-50 dark:bg-blue-900/20'
                            : 'hover:bg-gray-50 dark:hover:bg-slate-700'
                        }`}
                      >
                        <div className={`p-2 rounded-lg ${
                          isActive(item.path)
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 dark:bg-slate-600 text-gray-600 dark:text-gray-300'
                        }`}>
                          <Icon size={16} />
                        </div>
                        <div>
                          <p className={`text-sm font-bold ${
                            isActive(item.path)
                              ? 'text-blue-600 dark:text-blue-400'
                              : 'text-gray-900 dark:text-white'
                          }`}>
                            {item.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{item.desc}</p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Main Navigation */}
            {mainNavigation.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`relative px-4 py-2 text-sm font-bold rounded-lg transition-all duration-300 ${
                  isActive(item.path)
                    ? 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-400/10'
                    : 'text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-slate-800'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2">
            {/* Theme Toggle */}
            <button
              onClick={onThemeToggle}
              className="p-2 rounded-lg transition-all duration-300 hover:scale-110 hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-700 dark:text-yellow-400"
              aria-label="Toggle theme"
            >
              {darkMode ? <FiSun className="w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-700 dark:text-gray-300"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t py-4 border-gray-200 dark:border-gray-800">
            <div className="space-y-1">
              {/* Photo Editor Section */}
              <div className="px-4 py-2">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Photo Editor</p>
                {photoFeatures.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.path}
                      onClick={() => setIsMenuOpen(false)}
                      className={`flex items-center gap-3 w-full text-left px-4 py-3 text-sm font-bold rounded-lg transition-colors mb-1 ${
                        isActive(item.path)
                          ? 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-400/10'
                          : 'text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      <Icon size={18} />
                      {item.name}
                    </Link>
                  );
                })}
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>

              {/* Main Navigation */}
              {mainNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block w-full text-left px-4 py-3 text-sm font-bold rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-400/10'
                      : 'text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-slate-800'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
